import { ConfirmationDialogService } from 'src/app/services/confirmation-dialog.service';
import {
  AfterViewInit,
  ElementRef,
  Renderer2,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  ViewChild,
  inject
} from '@angular/core';
import { Component, OnInit, AfterViewChecked } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as jexcel from 'src/assets/lib/jexcel/jexcel.js';
import { isNil } from 'lodash-es';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { MessageService } from 'primeng/api';
import { Subscription } from 'rxjs/internal/Subscription';
import { setRolesToDelete } from 'src/app/STORE/actions/roles.actions';
import { rolesWithRefAndDeleted, saveRolesFailure } from 'src/app/STORE/selectors/roles.selector';
import { v4 as uuidv4 } from 'uuid';
import { AppLoaderService } from 'src/app/services/app-loader.service';
import { UtilService } from 'src/app/services/util.service';
import { RegexPatternConstants } from 'src/app/base/regex-pattern.constants';

@AutoUnsubscribe({ arrayName: 'subscriptions' })
@Component({
  selector: 'vb-vs-excel',
  templateUrl: './vs-excel.component.html',
  styleUrls: ['./vs-excel.component.scss'],
  standalone: false //NOSONAR
})
export class VsExcelComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges, AfterViewChecked {
  private confirmationDialogService = inject(ConfirmationDialogService);
  private messageService = inject(MessageService);
  private loader = inject(AppLoaderService);
  private zone = inject(NgZone);
  private translate = inject(TranslateService);
  private utilService = inject(UtilService);
  private store = inject(Store);
  private renderer = inject(Renderer2);
  private el = inject(ElementRef);

  @Input() columns: any = [];
  @Input() nestedHeaders: any = [];
  @Input() mergeHeader: any = [];
  @Input() freezeColumns = 0;
  @Input() sheetData: any = [];
  @Input() worksheets: any = [];
  @Input() showSearch = false;
  @Input() isTranslation = false;
  @Input() isRDBMS = false;
  @Input() disableFieldRows: any = [];
  @Input() allowedRolesRows: any = [];
  @Input() config: {
    events?: {
      onWhereUsedClick?: (data: string) => void;
    };
  } = {};
  @Output() public cellValueChange = new EventEmitter();
  @Output() public deleteEntireRow = new EventEmitter();
  @ViewChild('vsSheet') vsSheet: ElementRef;
  @Input() isRoles = false;
  @Input() component = null;
  @Input() pseudocodeStorage: Record<string, string> = {};
  initialLoad = true;
  disallowEdit = false;
  defaultSheetConfig: any = {
    data: [],
    defaultColWidth: '100px',
    lazyLoading: true,
    tableOverflow: true,
    tableWidth: '75vw',
    tableHeight: '75vh',
    columns: [],
    nestedHeaders: [],
    minDimensions: [this.columns.length - 1, 20],
    onchange: this.onCellValueChanges.bind(this),
    onbeforechange: this.onBeforeCellValueChanges.bind(this),
    onevent: this.onSheetClick.bind(this),
    onselection: this.onSelectionActive.bind(this),
    onbeforedeleterow: this.ondeleteEntireRow.bind(this),
    ondeleterow: this.onAfterDeleteRow.bind(this),
    onbeforeinsertrow: this.onbeforeinsertrow.bind(this),
    onbeforedeletecolumn: this.onbeforedeletecolumn,
    onbeforeinsertcolumn: this.onbeforeinsertcolumn,
    contextMenu: this.onClickContextMenu.bind(this),
    onundo: this.undoSheet.bind(this),
    onredo: this.undoSheet.bind(this),
    onpaste: this.onPaste.bind(this),
    onbeforepaste: this.onBeforePaste.bind(this),
    oncreateeditor: this.onCreateEditor.bind(this),
    onload: this.onLoad.bind(this),
    search: false,
    columnDrag: false,
    rowDrag: false
  };
  excelTable: any = null;
  ref$: Subscription = new Subscription();
  rownum: number;
  custColNo: number;
  pseudoColNo: number;
  whereUsedColNo: number;
  displayModal: boolean;
  readOnly: boolean;
  codeEditor: any;
  rowData: any;
  pseudoCodeCoord: any;
  editorOptions = { theme: 'vs-light', language: 'text/plain' };
  multipleSheets = false;
  tableName = '';
  uniqueCols: any = [];
  duplicateRow: number | undefined;
  mulDataSelCellEnd: any;
  //translation related variables
  pastingData: any;
  xcol: any;
  yrow: any;
  codes: any = [];
  customized: any = [];
  manual: any = [];
  prepasteKeyValues: string[] = [];
  preChangeKeyValue = '';
  hasReference = false;
  keywords = [
    'abstract',
    'assert',
    'as',
    'any',
    'boolean',
    'break',
    'byte',
    'case',
    'catch',
    'constructor',
    'char',
    'class',
    'const',
    'continue',
    'default',
    'do',
    'double',
    'debugger',
    'delete',
    'declare',
    'else',
    'enum',
    'extends',
    'export',
    'false',
    'final',
    'finally',
    'function',
    'from',
    'float',
    'for',
    'goto',
    'get',
    'if',
    'implements',
    'import',
    'instanceof',
    'int',
    'in',
    'interface',
    'long',
    'let',
    'native',
    'new',
    'null',
    'package',
    'private',
    'protected',
    'public',
    'return',
    'short',
    'static',
    'set',
    'strictfp',
    'string',
    'symbol',
    'super',
    'switch',
    'synchronized',
    'throw',
    'throws',
    'transient',
    'true',
    'try',
    'typeOf',
    'type',
    'void',
    'var',
    'volatile',
    'while',
    'with',
    'continue',
    'this',
    'yield',
    'module',
    'require',
    'number',
    'of',
    'date'
  ];
  sqlKeywords = [
    'add',
    'add constraint',
    'all',
    'alter',
    'alter column',
    'alter table',
    'and',
    'any',
    'as',
    'asc',
    'backup database',
    'between',
    'case',
    'check',
    'column',
    'constraint',
    'create',
    'create database',
    'create index',
    'create or replace view',
    'create table',
    'create procedure',
    'create unique index',
    'create view',
    'database',
    'default',
    'delete',
    'desc',
    'distinct',
    'drop',
    'drop column',
    'drop constraint',
    'drop database',
    'drop default',
    'drop index',
    'drop table',
    'drop view',
    'exec',
    'exists',
    'foreign key',
    'from',
    'full outer join',
    'group by',
    'having',
    'in',
    'index',
    'inner join',
    'inner',
    'insert into',
    'insert',
    'into',
    'full',
    'outer',
    'group',
    'by',
    'order',
    'foreign',
    'insert into select',
    'is null',
    'is not null',
    'join',
    'left join',
    'like',
    'limit',
    'not',
    'is',
    'null',
    'left',
    'primary',
    'key',
    'not null',
    'or',
    'order by',
    'outer join',
    'primary key',
    'procedure',
    'right join',
    'rownum',
    'select',
    'select distinct',
    'select into',
    'select top',
    'set',
    'table',
    'top',
    'truncate table',
    'truncate',
    'union',
    'union all',
    'unique',
    'update',
    'values',
    'view',
    'where',
    'backup',
    'replace'
  ];
  subscriptions: Subscription[] = [];
  scrollPosition = 0;

  private addAllowedRolesTooltips(): void {
    setTimeout(() => {
      const allowedRolesColIndex = this.columns.findIndex((col) => col.title === 'Allowed Roles');
      if (allowedRolesColIndex !== -1 && this.vsSheet?.nativeElement) {
        const cells = this.vsSheet.nativeElement.querySelectorAll(
          `.jexcel_content tbody tr td[data-x="${allowedRolesColIndex}"]`
        );

        cells.forEach((cell: HTMLElement) => {
          if (cell.innerText && cell.innerText.trim()) {
            this.renderer.setAttribute(cell, 'title', cell.innerText.replace(/;/g, ', '));
            this.renderer.addClass(cell, 'allowed-role');
          }
        });
      }
    }, 0);
  }

  ngAfterViewInit(): void {
    if (this.worksheets.length > 0) {
      this.multipleSheets = true;
    }
    if (this.showSearch) {
      // for the Search icon of "Sample Data"
      setTimeout(() => {
        const addClasses = (element: HTMLElement, classes: string[]) => {
          classes.forEach((cls) => this.renderer.addClass(element, cls));
        };
        const searchElements = this.el.nativeElement.querySelectorAll('.jexcel_search');
        searchElements.forEach((searchElement: HTMLElement) => {
          const parentElement = searchElement.parentElement;
          if (parentElement && !parentElement.querySelector('.search-icon')) {
            const spanElement = this.renderer.createElement('span');
            addClasses(spanElement, ['search-icon', 'sampledata-search-icon']);
            const iconElement = this.renderer.createElement('i');
            addClasses(iconElement, ['pi', 'pi-search']);
            this.renderer.appendChild(spanElement, iconElement);
            // Insert the span after the .jexcel_search input
            if (searchElement.nextSibling) {
              this.renderer.insertBefore(parentElement, spanElement, searchElement.nextSibling);
            } else {
              this.renderer.appendChild(parentElement, spanElement);
            }
          }
        });
      }, 0);
    }
    this.initilizeTable();
  }

  ngOnChanges(): void {
    if (this.worksheets.length > 0) {
      this.multipleSheets = true;
    } else if (this.vsSheet) {
      if (this.excelTable) {
        const excelTableContent = this.vsSheet.nativeElement?.getElementsByClassName('jexcel_content')[0];
        this.scrollPosition = excelTableContent?.scrollTop ?? 0;
        this.excelTable.destroy();
      }
      this.initilizeTable();
      this.addAllowedRolesTooltips();
    }
  }

  findIndexMethod(colName) {
    return this.columns.findIndex((col) => {
      return col.title === colName;
    });
  }
  getdisbleColInd(cols) {
    return cols.map((col) => {
      return this.findIndexMethod(col);
    });
  }
  disableCells(colNo) {
    const excelData = this.sheetData; //2d array
    let disbleColInd = [];
    let disbleColIntegration = [];
    if (this.isTranslation) {
      disbleColInd = this.getdisbleColInd(['Key']);
    } else {
      // get colNo of table name, service name, method, i/p, o/p, url, path params which needs to be disabled and save in array
      disbleColInd = this.getdisbleColInd([
        'Table Name',
        'Service Name',
        'Internal Service Name',
        'HTTP Method',
        'Method Input',
        'Method Output',
        'URL',
        'Path Params'
      ]);
      disbleColIntegration = this.getdisbleColInd([
        'Table Name',
        'Service Name',
        'Internal Service Name',
        'HTTP Method',
        'Method Input',
        'Method Output',
        'URL',
        'Path Params',
        'Needs Custom Coding',
        'Comments',
        'Pseudo Code'
      ]);
    }
    excelData.forEach((row, i) => {
      // if custom is false for the current row
      if (row[colNo] === false || !row[colNo]) {
        disbleColInd.forEach((cellNo) => {
          const cell = this.vsSheet.nativeElement.jexcel.getCellFromCoords(cellNo, i);
          $(cell).addClass('readonly');
        });
      }
      if (row[this.columns.length - 1] === 'INTEGRATION_SERVICE') {
        disbleColIntegration.forEach((cellNo) => {
          const cell = this.vsSheet.nativeElement.jexcel.getCellFromCoords(cellNo, i);
          $(cell).addClass('readonly');
        });
      }
    });
  }
  findMultipleindexes(arr, key?: any, value?: any) {
    let indArr: any = [];
    arr.forEach((col, i) => {
      if (key) {
        //array of objects
        if (col[key] === value) {
          indArr = [...indArr, i];
        }
      } //array of strings
      else if (
        col === value ||
        (typeof col === 'string' && typeof value === 'string' && col.toLowerCase() === value.toLowerCase())
      ) {
        indArr = [...indArr, i];
      }
    });
    return indArr;
  }
  onCreateEditor(el, cell, x, y) {
    if (this.columns[x].maxlength) {
      const config = this.columns[x].maxlength;
      cell.children[0].setAttribute('maxlength', config);
    }
  }

  initilizeTable() {
    if (this.worksheets.length === 0) {
      this.initSingleSheet();
    } else {
      this.initMultiSheet();
    }
    this.hidePredefinedSearchTextInJexcel();
  }

  private initSingleSheet() {
    if (!this.columns || this.columns.length === 0) {
      return;
    }
    this.defaultSheetConfig.columns = this.columns;
    this.defaultSheetConfig.nestedHeaders =
      this.nestedHeaders && this.nestedHeaders.length > 0 ? this.nestedHeaders : [];
    this.defaultSheetConfig.data = this.sheetData.length > 0 ? [...this.sheetData] : [];
    this.defaultSheetConfig.tableHeight = '70vh';
    this.defaultSheetConfig.search = this.showSearch;

    // Calculate table width from column widths; use 100% if it fits within viewport
    const totalColWidth = (this.columns || []).reduce((sum, col) => {
      return sum + (parseInt(col.width, 10) || 100);
    }, 50); // +50 for row number column
    const viewportWidth = window.innerWidth * 0.75;
    this.defaultSheetConfig.tableWidth = totalColWidth > viewportWidth
      ? totalColWidth + 'px'
      : '100%';

    if (this.freezeColumns > 0) {
      this.defaultSheetConfig.freezeColumns = this.freezeColumns;
    }
    if (this.component === 'services') {
      this.defaultSheetConfig.minDimensions = [this.columns.length, this.sheetData.length];
    }

    this.excelTable = jexcel(this.vsSheet.nativeElement, this.defaultSheetConfig);
    $('.jexcel_search').attr('placeholder', 'Search');
    // find whether to custom column is present for generated services
    this.custColNo = this.findIndexMethod('custom');
    if (this.custColNo > -1) {
      this.disableCells(this.custColNo);
    }
    this.applyRolesReadonly();

    this.pseudoColNo = this.columns.findIndex((col) => {
      return col.title === 'Pseudo Code';
    });
    this.whereUsedColNo = this.columns.findIndex((col) => {
      return col.title === this.translate.instant('WHERE_USED');
    });

    this.uniqueCols = this.findMultipleindexes(this.columns, 'unique', true);
    const defSortColNo = this.columns.findIndex((col) => {
      return col.sortByDefault;
    });
    if (defSortColNo > -1) {
      this.excelTable.orderBy(defSortColNo);
    }
    // title in every column is coming as undefined for tooltip so programmactically changing the title
    this.columns.forEach((col, i) => {
      const $headerCell = $(this.vsSheet.nativeElement).find(`.jexcel thead td[data-x="${i}"]`);
      if ($headerCell.length) {
        $headerCell.attr('title', col.title);
      }
    });
  }

  private applyRolesReadonly() {
    if (!this.isRoles) {
      return;
    }
    this.disableFieldRows.forEach((ele) => {
      const indexOfField = this.columns.findIndex((d) => d.title === 'Short Name');
      const excelRow = this.vsSheet.nativeElement.jexcel.getData().findIndex((col) => {
        return col[indexOfField] === ele;
      });

      if (excelRow > -1) {
        this.columns.forEach((element) => {
          const cell = this.vsSheet.nativeElement.jexcel.getCellFromCoords(element.name, excelRow);
          $(cell).addClass('readonly');
        });
      }
    });
  }

  private initMultiSheet() {
    this.defaultSheetConfig.search = true;
    this.defaultSheetConfig.columnDrag = true;
    this.worksheets = this.worksheets.map((sheet) => {
      return {
        ...this.defaultSheetConfig,
        ...sheet
      };
    });
    this.excelTable = jexcel.tabs(this.vsSheet.nativeElement, this.worksheets);
    this.vsSheet.nativeElement.children[0].querySelector('.jexcel_tab_link').click();
    $('.jexcel_search').attr('placeholder', 'Search');
  }

  hidePredefinedSearchTextInJexcel() {
    // Hiding the predefined "Search:" text in jexcel_filter div
    const filterDiv = this.el.nativeElement.querySelector('.jexcel_filter div:nth-child(2)');
    if (filterDiv && filterDiv.firstChild) {
      filterDiv.firstChild.nodeValue = ''; // Clear "Search:" text
    }
  }

  checkServiceDuplicate(worksheet, row) {
    const colData1 = worksheet.jexcel.getColumnData(1);
    const colData2 = worksheet.jexcel.getColumnData(2);
    if (row[1] && row[2]) {
      const count1 = this.findMultipleindexes(colData1, '', row[1]);
      const count2 = this.findMultipleindexes(colData2, '', row[2]);
      if (count1.length > 1 && count2.length > 1) {
        this.showMessage('error', 'Service Already Available', true);
        return true;
      }

      return false;
    }
    return false;
  }
  checkDuplicate(worksheet, colNo, value) {
    if (this.uniqueCols.includes(colNo)) {
      const colData = worksheet.jexcel.getColumnData(colNo);
      const empty = [undefined, null, ''];
      const trimData = colData.filter(function (a) {
        return !empty.includes(a);
      });
      const count = this.findMultipleindexes(trimData, '', value).length;
      if (count > 1) {
        this.showMessage('error', `${this.columns[colNo].title} Should be unique`, true);
        return true;
      }
      return false;
    }
    return false;
  }

  // Introduced to handle Delete operation on Roles and Translations when delete key from keyboard is pressed
  onBeforeCellValueChanges(worksheet: any, cell: any, x: number, y: any, value: any) {
    if (event?.['key'] === 'Delete' && this.isTranslation) {
      // Temporary fix to prevent data deletion in Translation
      throw 'Cell data Deletion Detected';
    }
    // Capture the current key value before it gets overwritten, so it can be restored on duplicate
    if (this.isTranslation) {
      this.preChangeKeyValue = worksheet.jexcel.options.data[Number(y)]?.[Number(x)] ?? '';
    }
    if (
      event?.['key'] === 'Delete' &&
      (this.columns[1].title === 'Role Name' || this.columns[2].title === 'Short Name')
    ) {
      if (worksheet.jexcel.selectedContainer[0] === 1 && worksheet.jexcel.selectedContainer[2] === 3) {
        this.deleteSelectedRoles(worksheet.jexcel);
        // Temporary fix to prevent data deletion before API call
        throw 'Multiple Row Deletion Detected';
      } else if (
        (worksheet.jexcel.selectedContainer[0] === 1 && worksheet.jexcel.selectedContainer[2] === 2) ||
        worksheet.jexcel.selectedContainer[3] - worksheet.jexcel.selectedContainer[1] > 0
      ) {
        // Temporary fix to prevent data deletion
        this.zone.run(() => {
          this.showMessage('error', 'All columns should be selected to delete rows', true);
        });
        throw 'Prevent deletion on multiple select';
      }
    }
  }

  //update modifiedRows and modifiedcolumns for sample data
  updateRowsAndCols(sheetIndex, currowData, x, y) {
    const curSheetCols = this.worksheets[sheetIndex]?.columns;
    let modifiedRowNumbers = this.worksheets[sheetIndex]?.modifiedRowNumbers;
    let modifiedColumns = this.worksheets[sheetIndex]?.modifiedColumns;
    let modifiedRows: Record<number, string[]> = {};
    modifiedRows = this.worksheets[sheetIndex]?.modifiedRows;
    const fieldsMapping = JSON.parse(this.worksheets[sheetIndex]?.fieldsMapping);
    const colName = this.worksheets[sheetIndex]?.columns[Number(x)]?.id;
    const colSid = fieldsMapping[colName];
    const rowNumber = currowData[curSheetCols.length - 1] ? currowData[curSheetCols.length - 1] : Number(y) + 1;
    if (rowNumber <= 10) {
      if (!Object.prototype.hasOwnProperty.call(modifiedRows, y)) {
        modifiedRows = { ...modifiedRows, [y]: [colSid] };
      } else {
        modifiedRows = { ...modifiedRows, [y]: Array.from(new Set([...modifiedRows[y], colSid])) };
      }
    }

    if (!modifiedRowNumbers.includes(rowNumber) && rowNumber <= 10) {
      modifiedRowNumbers = [...modifiedRowNumbers, rowNumber];
    }

    if (!modifiedColumns.includes(colName) && rowNumber <= 10) {
      modifiedColumns = [...modifiedColumns, colName.toLowerCase()];
    }
    const updWorksheet = this.worksheets.map((wk, i) => {
      if (i === Number(sheetIndex)) {
        wk.modifiedRowNumbers = modifiedRowNumbers;
        wk.modifiedColumns = modifiedColumns;
        wk.modifiedRows = modifiedRows;
      }
      return wk;
    });
    this.worksheets = [...updWorksheet];
  }

  // update function on cell value changes
  onCellValueChanges(worksheet: any, cell: any, x: number, y: any, value: any) {
    const xldata = worksheet.jexcel.getData(); // 2d array
    const currowData = xldata[Number(y)];
    if (this.multipleSheets) {
      const sheetIndex = this.vsSheet.nativeElement.children[0]
        .querySelector('.selected')
        .getAttribute('data-spreadsheet');
      const cellName = jexcel.getColumnNameFromId([x, y]);
      const data = this.vsSheet.nativeElement.jexcel[sheetIndex].getData();
      const rowNumberCol = this.worksheets[sheetIndex]?.columns.length - 1;
      const rowNumber = currowData[rowNumberCol] ? currowData[rowNumberCol] : Number(y) + 1;
      data[y][rowNumberCol] = rowNumber;
      this.vsSheet.nativeElement.jexcel[sheetIndex].setData(data);
      //update modified row num and column name in sheet
      this.updateRowsAndCols(sheetIndex, currowData, x, y);
      // check whether the event is paste
      // check whether delete is pressed and has multiple selections
      // When both condition fails, update the value
      if (!(event?.type === 'paste' || ((event as any).which === 46 && cellName !== this.mulDataSelCellEnd))) {
        const selCells = this.vsSheet.nativeElement.children[1].querySelectorAll('.selection');
        //check whether multiple cells selected before save
        if (selCells.length > 0) {
          // if multiple cells are selected, update the value on the last cell
          const xval = selCells[selCells.length - 1].getAttribute('data-x');
          const yval = selCells[selCells.length - 1].getAttribute('data-y');
          if (xval === x && yval === y) {
            this.cellValueChange.emit({
              sheetIndex: sheetIndex,
              sheetData: data,
              modifiedRows: this.worksheets[sheetIndex]?.modifiedRows,
              modifiedRowNumbers: this.worksheets[sheetIndex]?.modifiedRowNumbers,
              modifiedColumns: this.worksheets[sheetIndex]?.modifiedColumns
            });
          }
        } else {
          this.cellValueChange.emit({
            sheetIndex: sheetIndex,
            sheetData: data,
            modifiedRows: this.worksheets[sheetIndex]?.modifiedRows
          });
        }
      }
    } else if (this.isTranslation) {
      const cellName = jexcel.getColumnNameFromId([x, y]);
      // check whether the event is paste
      // check whether delete is pressed and has multiple selections
      if (!(event?.type === 'paste' || ((event as any).which === 46 && this.excelTable.getHighlighted().length > 1))) {
        const code = this.columns[Number(x)].code;
        if (currowData[this.columns.length - 1] === false) {
          this.cellValueChange.emit({
            code: code,
            customized: [currowData[0]],
            manual: [],
            sheetData: xldata,
            delete: false
          });
        } else {
          const colInd = this.columns.findIndex((col) => {
            return col.code === code;
          });
          if (currowData[0] !== '' && currowData[colInd] !== '') {
            if (currowData[this.columns.length - 1] === '') {
              worksheet.jexcel.options.data[Number(y)][this.columns.length - 1] = true;
            }
            if (!this.checkDuplicate(worksheet, colInd, currowData[0])) {
              this.cellValueChange.emit({
                code: code,
                manual: [currowData[0]],
                customized: [],
                sheetData: xldata,
                delete: false
              });
            } else {
              this.showMessage('error', 'Key should be unique', true);
              // Restore the original value (before edit) instead of clearing to empty
              const originalValue = this.preChangeKeyValue ?? '';
              worksheet.jexcel.options.data[Number(y)][Number(x)] = originalValue;
              const dupCell = worksheet.jexcel.getCellFromCoords(x, y);
              if (dupCell) {
                dupCell.innerHTML = originalValue;
              }
            }
          } else {
            this.showMessage('error', 'Key is mandatory', true);
          }
        }
      } else if ((event as any).which === 46 && this.excelTable.getHighlighted().length > 1) {
        if (Number(x) > 0) {
          if (!this.codes.includes(this.columns[Number(x)].code)) {
            this.codes = [...this.codes, this.columns[Number(x)].code];
          }
        }
        if (cellName !== this.mulDataSelCellEnd) {
          this.codes.forEach((code) => {
            this.cellValueChange.emit({
              code: code,
              manual: this.manual,
              customized: this.customized,
              sheetData: xldata,
              delete: false
            });
          });
        }
      }
    } else if (!this.checkDuplicate(worksheet, Number(x), value)) {
      // update if uuid is available
      // check roles validation
      if (this.roleNameVal(currowData)) {
        return;
      }
      if (currowData[0]) {
        if (
          (this.columns[1].title === 'Role Name' || this.columns[2].title === 'Short Name') &&
          event?.['key'] === 'Delete'
        ) {
          this.handleRoleDelete(currowData, worksheet);
        } else {
          // check 'ALL' for allowed roles in services
          if (this.component === 'services') {
            const colInd = this.columns.findIndex((col) => col.title === 'Allowed Roles');
            if (Number(x) === colInd && value.includes('All')) {
              cell.innerText = 'All';
            }
          }
          this.cellValueChange.emit({ data: currowData, action: 'update' });
        }
      } else {
        // check if the entire row has duplicate
        let isDuplicate = false;
        this.columns.forEach((col, i) => {
          if (this.checkDuplicate(worksheet, i, currowData[i])) {
            isDuplicate = true;
          }
        });
        this.duplicateRow = isDuplicate ? this.duplicateRow : undefined;
        if (
          this.columns.findIndex((col) => {
            return col.title.includes('Service');
          }) > -1
        ) {
          if (this.checkServiceDuplicate(worksheet, currowData)) {
            this.duplicateRow = Number(y);
          }
        }
        if (this.rownum >= 0 && Number(y) >= 0 && this.rownum !== Number(y) && this.duplicateRow !== this.rownum) {
          // save the previous row
          const prevRowData = xldata[this.rownum];
          this.saveDataRowWise(prevRowData);
        }
        // save the row number
        this.rownum = Number(y);
      }
    } else {
      this.duplicateRow = Number(y);
    }
    this.addAllowedRolesTooltips();
  }

  // Handle roles deletion on delete key
  handleRoleDelete(currowData, worksheet) {
    if (!currowData[1] && !currowData[2] && !currowData[3]) {
      this.deleteSelectedRoles(worksheet.jexcel);
    } else if (!currowData[1] && !currowData[2]) {
      this.zone.run(() => {
        this.showMessage('error', 'Role Name and Short Name are mandatory', true);
      });
    } else if (!currowData[1]) {
      this.zone.run(() => {
        this.showMessage('error', 'Role Name is mandatory', true);
      });
    } else if (!currowData[2]) {
      this.zone.run(() => {
        this.showMessage('error', 'Short Name is mandatory', true);
      });
    }
  }
  roleNameVal(currowData) {
    if (this.columns[1].title === 'Role Name' || this.columns[2].title === 'Short Name') {
      const roleName = currowData[1].trim().replace(/\s\s+/g, ' ').toLowerCase();
      const shortName = currowData[2].trim().replace(/\s\s+/g, ' ').toLowerCase();
      if (this.checkAlphanumeric(currowData[1]) || this.checkAlphanumeric(currowData[2])) {
        this.showMessage('error', this.translate.instant('ALPHANUMEREIC_CHARACTERS_FOR_ROLES'), true);
        return true;
      } else if (this.keywords.includes(roleName) || this.keywords.includes(shortName)) {
        this.showMessage('error', 'Name should not be a java keyword', true);
        return true;
      } else if (this.isRDBMS && (this.sqlKeywords.includes(roleName) || this.sqlKeywords.includes(shortName))) {
        this.showMessage('error', 'Name should not be a sql keyword', true);
        return true;
      }
    }
    return false;
  }

  // Regular expression check for alphanumeric validation
  // Written as a standalone method to avoid error when using same reference of re on exec method
  checkAlphanumeric(data: string): boolean {
    const myRe = RegexPatternConstants.alphanumericName;
    return data ? !myRe.exec(data) : false;
  }
  // save function if the event came out of the row
  saveDataRowWise(currowData) {
    // to avoid addition of same rows
    if (currowData[0] === '') {
      // get required columns indexes
      const indexs = this.columns
        .filter((x) => {
          return x.validation === 'required';
        })
        .map((col) => {
          return this.columns.findIndex((x) => {
            return col.title === x.title;
          });
        });
      let temp = 0,
        cols = '';
      for (let i = 0; i < indexs.length; i++) {
        if (currowData[indexs[i]] !== '') {
          temp++;
        } else {
          cols = `${cols + this.columns[indexs[i]].title} `;
        }
      }
      // check roles validation
      if (this.roleNameVal(currowData)) {
        return;
      }
      // check whether all required columns are entered, key is duplicate/not and then create uuid for the row
      if (temp === indexs.length && this.duplicateRow !== this.rownum) {
        currowData[0] = uuidv4();
        const data = this.vsSheet.nativeElement.jexcel.getData();
        data[this.rownum][0] = currowData[0];
        if (
          this.columns.findIndex((col) => {
            return col.title.includes('Service');
          }) > -1
        ) {
          data[this.rownum][this.custColNo] = true;
        }
        const empty = [undefined, null, ''];
        const trimData = data.filter(function (a) {
          return a.some(function (val) {
            return !empty.includes(val);
          });
        });
        this.sheetData = trimData.map((row) => {
          const row1 = row.map((dt) => {
            return typeof dt === 'string' ? dt.trim().replace(/\s\s+/g, ' ') : dt;
          });
          return row1;
        });
        this.vsSheet.nativeElement.jexcel.setData(this.sheetData);
        if (this.custColNo > -1) {
          this.disableCells(this.custColNo);
        }
        this.columns.forEach((col, i) => {
          if (col.type === 'text') {
            currowData[i] = currowData[i].trim().replace(/\s\s+/g, ' ');
          }
        });
        this.cellValueChange.emit({ data: currowData, action: 'save' });
        // Validation for role short name when already present in data model
        const sub = this.store.select(saveRolesFailure).subscribe((val: any) => {
          if (val && val.sid === currowData[0]) {
            const index = this.sheetData.map((dt) => dt[0]).indexOf(currowData[0]);
            this.sheetData[index][0] = '';
            this.vsSheet.nativeElement.jexcel.setData(this.sheetData);
            this.rownum = -1;
          }
        });
        this.subscriptions.push(sub);
      } else {
        // check if the row is empty, then donot throw error message
        const emptArr = currowData.filter((data) => {
          return data !== '';
        });
        if (emptArr.length !== 0 && cols) {
          this.showMessage('error', `${cols}is Mandatory`, true);
          this.rownum = 0;
        }
      }
    }
  }
  onSheetClick(event: any, worksheet: any, cell: any, x: any, y: any, y2: any) {
    if (event === 'onselection' && this.rownum >= 0 && x >= 0 && this.rownum !== x) {
      const xldata = worksheet.jexcel.getData(); // 2d array
      const currowData = xldata[this.rownum];
      this.saveDataRowWise(currowData);
    }
    if (event === 'onselection' && this.pseudoColNo > 0 && this.pseudoColNo === y && !this.disallowEdit) {
      this.codeEditor = this.vsSheet.nativeElement.jexcel.getData(y, x, false)[0][0];
      this.displayModal = true;
      this.pseudoCodeCoord = { x: y, y: x };
    }

    // Check for Where Used click (visible column 12 = data column 14)
    if (event === 'onselection' && this.whereUsedColNo === y) {
      const rowData = worksheet.jexcel.getData()[x];
      if (rowData && rowData[0] && this.config?.events?.onWhereUsedClick) {
        this.config.events.onWhereUsedClick(rowData[0]); // rowData[0] is the service ID
      }
    }
    if (event === 'onselection' && (this.multipleSheets || this.isTranslation)) {
      this.mulDataSelCellEnd = jexcel.getColumnNameFromId([y, y2]);
    }
  }
  onSelectionActive(instance, x1, y1, x2, y2, origin) {
    const cellName1 = jexcel.getColumnNameFromId([x1, y1]);
    const cellName2 = jexcel.getColumnNameFromId([x2, y2]);
    $('#log').append(`The selection from ${cellName1} to ${cellName2}`);
    const cellValue = instance.jexcel.getData()?.[y1]?.[x1];
    const cellValueStr = typeof cellValue === 'string' ? cellValue : '';
    if (
      cellName1 === cellName2 &&
      cellValueStr &&
      cellValueStr.indexOf('delete-row') > -1
    ) {
      this.confirmDeletion([y1], [instance.jexcel.getData()[y1][0]]);
    } else if (
      cellName1 === cellName2 &&
      cellValueStr &&
      cellValueStr.indexOf('psuedo-code') > -1 &&
      !this.disallowEdit
    ) {
      this.rowData = instance.jexcel.getData()[y1];
      this.pseudoCodeCoord = { x: x1, y: y1 };
      const PSEUDO_CODE_INDEX = 13;
      const SPLIT_INDEX = 3;
      const serviceId = this.rowData[PSEUDO_CODE_INDEX]?.split("'")?.[SPLIT_INDEX];
      if (serviceId) {
        this.codeEditor = this.getPseudocodeByServiceId(serviceId);
      } else {
        this.codeEditor = '';
      }
      this.displayModal = true;
    } else if (
      cellName1 === cellName2 &&
      cellValueStr &&
      cellValueStr.indexOf('where-used-for-service') > -1
    ) {
      const rowData = instance.jexcel.getData()[y1];
      const WHERE_USED_INDEX = 14;
      const SPLIT_INDEX = 3;
      const serviceId = rowData?.[WHERE_USED_INDEX]?.split("'")?.[SPLIT_INDEX];
      if (serviceId && this.config?.events?.onWhereUsedClick) {
        this.config.events.onWhereUsedClick(serviceId);
      }
    }
  }

  // Add method to retrieve pseudocode by service ID
  getPseudocodeByServiceId(serviceId: string): string {
    return this.pseudocodeStorage[serviceId] || '';
  }

  // get the data, rowstart, column start before pasting for translation screen
  onBeforePaste(instance, data, x, y) {
    if (this.isTranslation) {
      this.pastingData = data;
      this.xcol = Number(x);
      this.yrow = Number(y);
      // Capture original values before paste overwrites them so they can be restored on duplicate
      const code = this.columns[Number(x)].code;
      const colInd = this.columns.findIndex((col) => col.code === code);
      const rowCount = data.split('\n').length;
      const currentData = instance.jexcel.getData();
      this.prepasteKeyValues = [];
      for (let i = 0; i < rowCount; i++) {
        this.prepasteKeyValues.push(currentData[Number(y) + i]?.[colInd] ?? '');
      }
    }
  }
  // pasting many cells in sample data and translation in a single call
  onPaste(worksheet: any) {
    const xldata = worksheet.jexcel.getData();
    if (this.multipleSheets) {
      const sheetIndex = this.vsSheet.nativeElement.children[0]
        .querySelector('.selected')
        .getAttribute('data-spreadsheet');
      this.cellValueChange.emit({
        sheetIndex: sheetIndex,
        sheetData: xldata,
        modifiedRowNumbers: this.worksheets[sheetIndex]?.modifiedRowNumbers,
        modifiedColumns: this.worksheets[sheetIndex]?.modifiedColumns,
        modifiedRows: this.worksheets[sheetIndex]?.modifiedRows
      });
    } else if (this.isTranslation) {
      const rowInds = this.pastingData.split('\n');
      const colInds = rowInds[0].split('\t');
      colInds.map((colData, i) => {
        this.codes = [...this.codes, this.columns[i + Number(this.xcol)].code];
      });
      const pasteCode = this.columns[Number(this.xcol)].code;
      const colInd = this.columns.findIndex((col) => col.code === pasteCode);
      rowInds.forEach((data, i) => {
        if (xldata[i + Number(this.yrow)][colInd]) {
          if (!this.checkDuplicate(worksheet, colInd, xldata[i + Number(this.yrow)][colInd])) {
            if (xldata[i + Number(this.yrow)][this.columns.length - 1] === false) {
              this.customized = [...this.customized, xldata[i + Number(this.yrow)][colInd]];
            } else {
              this.manual = [...this.manual, xldata[i + Number(this.yrow)][colInd]];
            }
          } else {
            // Restore the original value (before paste) to preserve existing data and prevent autosave
            const dupRowIndex = i + Number(this.yrow);
            const originalValue = this.prepasteKeyValues[i] ?? '';
            xldata[dupRowIndex][colInd] = originalValue;
            const dupCell = worksheet.jexcel.getCellFromCoords(colInd, dupRowIndex);
            if (dupCell) {
              dupCell.innerHTML = originalValue;
            }
            this.showMessage('error', 'Key should be unique', false);
          }
        } else {
          this.showMessage('error', 'Key is mandatory', false);
        }
      });
      if (this.manual.length > 0 || this.customized.length > 0) {
        this.codes.forEach((code) => {
          this.cellValueChange.emit({
            code: code,
            manual: this.manual,
            customized: this.customized,
            sheetData: xldata,
            delete: false
          });
        });
      }
      this.codes = [];
      this.manual = [];
      this.customized = [];
    }
  }
  // getReferences(id, apiName) {
  //   const promise = new Promise<void>((resolve, reject) => {
  //     this.ref$ = this.bs.get(ApiConstants[apiName], { id: id }).subscribe({
  //       next: (res: any) => {
  //         resolve(res);
  //       },
  //       error: (err: any) => {
  //         reject(err);
  //       },
  //       complete: () => {
  //         console.log('complete');
  //       },
  //     });
  //     // this.addToSubscriptionsList(this.ref$);
  //   });
  //   return promise;
  // }

  // formConfirmationMessage(rolename, value) {
  //   let messageObj = `Are you sure you want to delete the Role? `
  //   let message = "";
  //   let keys = Object.keys(value);
  //   let keysLength = keys.length;
  //   if (keysLength===0) {
  //     message = messageObj;
  //   } else {
  //     for (let i = 0; i < keysLength; i++) {
  //       let id = keys[i];
  //       let obj = value[id];
  //       for (let j in obj) {
  //         let refObj = obj[j];
  //         let names = Object.values(refObj.info).join();
  //         if (names && (refObj.objectType==='Workflow' || refObj.objectType==='WorkflowActionStep' || refObj.objectType==='WorkflowActorStep')) {
  //           message += `${rolename} is used in ${names} in Workflow. `;
  //         }
  //       }
  //       if (message.length > 0) {
  //         message += `To continue with deletion, please remove the reference and try to delete again.`;
  //         this.hasReference = true;
  //       } else {
  //         message += messageObj;
  //       }
  //     }
  //   }
  //   return message;
  // }

  confirmDeletion(rownums, ids) {
    const message = `Are you sure you want to delete the Role(s)? `;
    const confirmationObj = {
      message,
      accept: () => {
        this.deleteRoles(rownums, ids);
        return;
      },
      reject: () => {
        return;
      }
    };
    // if (this.hasReference) {
    //   confirmationObj = {
    //     message,
    //     acceptLabel: 'OK',
    //     accept: () => {
    //       that.hasReference = false;
    //       return;
    //     },
    //     rejectVisible: false,
    //   }
    // } else {
    //   confirmationObj = {
    //     message,
    //     accept: () => {
    //       that.deleteRoles(rownums, ids);
    //       return;
    //     },
    //     reject: () => {
    //       return;
    //     }
    //   }
    // }
    const _self = this;
    this.zone.run(() => {
      _self.confirmationDialogService.confirm(confirmationObj);
    });
  }

  // Delete roles from Database
  deleteRoles(rownums, ids) {
    // for (let i = 0; i < rownums.length; i++) {
    //this.excelTable.deleteRow(rownums[0], rownums.length);
    // }
    this.deleteEntireRow.emit(ids);
    this.loader.show();
    this.processRolesDeletion(rownums);
  }

  // To Remove roles deleted from database and retain roles with references
  processRolesDeletion(rownums: any) {
    const sub = this.store.select(rolesWithRefAndDeleted).subscribe((roles: any) => {
      if (!isNil(roles) && !isNil(rownums)) {
        if (roles.NotDeleted.length === 0) {
          this.showMessage('success', 'Role(s) Deleted Successfully !', true);
        } else if (roles.NotDeleted.length === 1) {
          this.showMessage(
            'error',
            `Role ${roles.NotDeleted[0].name} ${this.translate.instant('ROLE_HAS_REFERENCE')}`,
            true
          );
        } else if (roles.NotDeleted.length > 1) {
          const roleNames = roles.NotDeleted.map((role) => role.name).join(', ');
          this.showMessage('error', `Roles ${roleNames} ${this.translate.instant('ROLES_HAVE_REFERENCE')}`, true);
        }
        if (roles.Deleted.length > 0 && rownums.length > 0) {
          const roleIdsToDelete = roles.Deleted.map((role) => role.sid);
          rownums.reverse().forEach((rownum) => {
            if (roleIdsToDelete.includes(this.excelTable.getRowData(rownum)[0])) {
              this.excelTable.deleteRow(rownum);
            }
          });
        }
        this.loader.hide();
        this.store.dispatch(
          setRolesToDelete({
            rolesWithRefAndDeleted: null,
            filterRoles: true
          })
        );
      }
    });
    this.subscriptions.push(sub);
  }

  showMessage(status: string, message: string, clear: boolean, detail?: string) {
    if (clear) {
      this.messageService.clear();
    }
    this.messageService.add({
      life: 10000,
      severity: status,
      summary: message,
      detail: detail
    });
  }

  ondeleteEntireRow(worksheet: any, x: number, n: number, rowDOMElements: any, rowData: any, cellAttributes: any) {
    const data = worksheet.jexcel.getData(); // 2d array
    if (this.multipleSheets) {
      const sheetIndex = this.vsSheet.nativeElement.children[0]
        .querySelector('.selected')
        .getAttribute('data-spreadsheet');
      this.cellValueChange.emit({
        sheetIndex: sheetIndex,
        sheetData: data,
        modifiedRowNumbers: this.worksheets[sheetIndex]?.modifiedRowNumbers,
        modifiedColumns: this.worksheets[sheetIndex]?.modifiedColumns,
        modifiedRows: this.worksheets[sheetIndex]?.modifiedRows
      });
      return true;
    } else if (this.isTranslation) {
      if (event?.['key'] === 'Delete') {
        if (data[Number(x)][this.columns.length - 1]) {
          return true;
        }
        return false;
      }
      return true;
    }
    let row = Number(x);
    const ids: any = [];
    const names: any = [];
    for (let i = 0; i < n; i++) {
      if (data[row][0]) {
        if (this.custColNo < 0 || (this.custColNo > -1 && data[row][this.custColNo])) {
          ids.push(data[row][0]);
          names.push(data[row][1]);
        } else {
          return false;
        }
      }
      row++;
    }
    if (ids.length > 0 && !(this.columns[1].title === 'Role Name' || this.columns[2].title === 'Short Name')) {
      this.deleteEntireRow.emit(ids);
    } else if (
      (this.columns[1].title === 'Role Name' || this.columns[2].title === 'Short Name') &&
      event?.['key'] === 'Delete'
    ) {
      this.deleteSelectedRoles(worksheet.jexcel);
      return false;
    }
    return true;
  }
  onbeforedeletecolumn(worksheet: object, columnNumber: number, numOfColumns: number) {
    return false;
  }

  onbeforeinsertcolumn(worksheet: object, columnNumber: number, numOfColumns: number) {
    return false;
  }

  onbeforeinsertrow() {
    if (this.component === 'services') {
      return false;
    }
    return true;
  }

  undoSheet(worksheet: any, historyRecord: any) {
    if (historyRecord) {
      const xldata = worksheet.jexcel.getData();
      if (this.multipleSheets) {
        const sheetIndex = this.vsSheet.nativeElement.children[0]
          .querySelector('.selected')
          .getAttribute('data-spreadsheet');
        this.cellValueChange.emit({
          sheetIndex: sheetIndex,
          sheetData: xldata,
          modifiedRowNumbers: this.worksheets[sheetIndex]?.modifiedRowNumbers,
          modifiedColumns: this.worksheets[sheetIndex]?.modifiedColumns,
          modifiedRows: this.worksheets[sheetIndex]?.modifiedRows
        });
      } else if (historyRecord.records[0]) {
        const rowIndex = historyRecord.records[0].row;
        const rowData = xldata[Number(rowIndex)];
        if (rowData[0]) {
          // updates/ delete the row
          this.cellValueChange.emit({ data: rowData, action: 'update' });
        }
      }
    }
  }

  onClickContextMenu(worksheet: any, col: any, row: any, event: object, items: any, section: any, a1: any, a2: any) {
    items = [];
    const _self = this;
    // no context items are required for row
    if (row && (col === null || col === '') && this.component !== 'services') {
      items.push({
        title: 'Insert New Row',
        onclick: function () {
          worksheet.insertRow(1, parseInt(row));
        }
      });
      if (this.custColNo > -1) {
        const data = worksheet.getData();
        const currow = data[Number(row)];
        if (currow[this.custColNo]) {
          items.push({
            title: 'Delete Selected rows',
            onclick: function () {
              if (_self.isTranslation) {
                _self.deleteSelectedTranslations(worksheet,row, true);
              } else {
                worksheet.deleteRow();
              }
            }
          });
        }
      } else if (this.excelTable.selectedRow === row) {
        // Conditional check for a row selected by left click and the row that gets right clicked
        let hasData = true;
        worksheet.getSelectedRows().forEach((row) => {
          // Set value to false when multiple rows are selected and there is no id in atleast one row
          if (!row.cells[1].innerText) {
            hasData = false;
          }
        });
        if (hasData) {
          items.push({
            title: 'Delete Selected rows',
            onclick: function () {
              if (_self.columns[1].title === 'Role Name' || _self.columns[2].title === 'Short Name') {
                _self.deleteSelectedRoles(worksheet);
              } else {
                worksheet.deleteRow();
              }
            }
          });
        }
      }
    } else {
      items = [];
    }
    return items;
  }

  // Introduced to handle deletion in translations via delete key in keyboard
  onAfterDeleteRow(worksheet: any, x: number, n: number, rowDOMElements: any, rowData: any, cellAttributes: any) {
    if (event?.['key'] === 'Delete' && this.isTranslation) {
      this.deleteSelectedTranslations(worksheet.jexcel, -1, false);
    }
  }
  // Delete manual records from selected translations
  // From context menu flag is used since if delete is from context menu  we have remove the rows
  // If from delete button the rows will be automatically removed
  deleteSelectedTranslations(worksheet: any, row:number, fromContextMenu: boolean) {
    if (fromContextMenu) {
      if(worksheet.getSelectedRows().length > 0){
        worksheet.getSelectedRows().forEach((row) => {   
            const rownum = Number(row.getAttribute('data-y'));
            worksheet.deleteRow(rownum);
            this.manual = [...this.manual, row.cells[1].innerText];     
        });
      }else if(row){
        worksheet.deleteRow(row);
        const data = worksheet.getData();
        const currow = data[Number(row)];
        this.manual = [...this.manual, currow[1]];
        worksheet.setData(data);

      }
    }
    for (let i = 1; i < this.columns.length - 1; i++) {
      if (!this.codes.includes(this.columns[i].code)) {
        this.codes = [...this.codes, this.columns[i].code];
      }
    }
    this.codes.forEach((code) => {
      this.cellValueChange.emit({
        code: code,
        manual: this.manual,
        customized: this.customized,
        sheetData: worksheet.getData(),
        delete: true
      });
      this.manual = [];
    });
  }

  // Delete selected roles from database upon confirmation
  deleteSelectedRoles(worksheet: any) {
    const ids: any = [];
    const rolename: any = [];
    const rownums: any = [];
    worksheet.getSelectedRows().forEach((row) => {
      if (row.cells[1].innerText) {
        rownums.push(Number(row.getAttribute('data-y')));
        ids.push(row.cells[1].innerText);
        rolename.push(row.cells[2].innerText);
      }
    });
    if (ids.length) {
      // _self.loader.show();
      // _self.getReferences(ids, "checkRolesReferences").then((value: any) => {
      //   _self.confirmDeletion(rownums, rolename, ids);
      // }, (error) => {
      //   console.log(error);
      //   this.loader.hide();
      // });
      this.confirmDeletion(rownums, ids);
    }
  }
  submitCode() {
    this.rowData[13] = this.codeEditor;
    this.cellValueChange.emit({ data: this.rowData, action: 'update' });
    this.displayModal = false;
  }

  ngOnInit(): void {
    if (!this.utilService.checkEditPermission('services')) {
      this.disallowEdit = true;
    }

    this.setReadOnlyPseudoCode();
  }
  ngAfterViewChecked() {
    const _this = this;
    if (this.sheetData.length === 0) {
      // Create the newDiv element

      const newDiv = $('<div id="no-content" class="noDivs" style="display: flex; justify-content: center;">').html(
        'No records found'
      );
      if ($('table.jexcel tbody tr').length === 0) {
        $('table.jexcel tbody').append($('<tr>').append($('<td>').attr('colspan', '16').append(newDiv)));
      }
    }
    if (this.worksheets.length === 0) {
      $('.jexcel_search')
        .off('keyup')
        .on('keyup', function () {
          const searchValues = $('.jexcel_search').val();
          if (searchValues !== '' && searchValues !== undefined) {
            if ($('.input-clear-icon').length === 0) {
              $('.jexcel_search')
                .parent()
                .append('<span class="input-clear-icon"><img src="assets/images/svg/icon_clear.svg"/></span>');
            }
          } else {
            $('.input-clear-icon').remove();
          }
        });
      $('.input-clear-icon')
        .off('click')
        .on('click', function () {
          $('.jexcel_search').val('');
          $('.input-clear-icon').remove();
          _this.excelTable.resetSearch();
        });
    } else {
      const selectedIndex = parseInt($('.spreadsheet  .selected')?.attr('data-spreadsheet') ?? '0');
      $('.jexcel_search')
        .eq(selectedIndex)
        .off('keydown')
        .on('keydown', function () {
          $('.noDivs').remove();
          setTimeout(() => {
            const searchValues = $('.jexcel_search').eq(selectedIndex).val();
            console.log(searchValues);
            if (searchValues !== '' && searchValues !== undefined) {
              if ($('.input-clear-icon').eq(selectedIndex).length === 0) {
                $('.jexcel_search')
                  .eq(selectedIndex)
                  .parent()
                  .append('<span class="input-clear-icon"><img src="assets/images/svg/icon_clear.svg"/></span>');
              }
            } else {
              $('.input-clear-icon').eq(selectedIndex).remove();
            }
          }, 100);
          const newDiv = $('<div id="no-content" class="noDivs" style="display: flex ;justify-content: center;">').html(
            'No records found'
          );

          setTimeout(() => {
            const selectedTab = parseInt($('.selected').attr('data-spreadsheet') ?? '0', 10);
            console.log(selectedTab);
            const dynamicJexcel = `.jexcel_content tbody:eq(${selectedTab}) tr`;
            const dynamicNoContent = `.jexcel_tab:eq(${selectedTab}) div#no-content `;
            const afterDiv = `.jexcel_content:eq(${selectedTab})`;
            // console.log(dynamicJexcel);
            // console.log($(dynamicJexcel).length);
            // console.log(dynamicNoContent);
            // console.log($(dynamicNoContent).length);
            if ($(dynamicJexcel).length === 0 && $(dynamicNoContent).length < 1) {
              console.log('got it');
              $(afterDiv).after(newDiv);
            } else if ($('.jexcel_content tbody tr').length !== 0) {
              $('#no-content').remove();
            }
          }, 500);
        });
      $('.input-clear-icon')
        .eq(selectedIndex)
        .off('click')
        .on('click', function () {
          $('.jexcel_search').eq(selectedIndex).val('');
          $('.input-clear-icon').eq(selectedIndex).remove();
          _this.vsSheet.nativeElement.jexcel[selectedIndex].resetSearch();
        });
    }
  }
  onShow(event) {
    window.dispatchEvent(new Event('resize'));
  }

  onResizeEnd(event) {
    window.dispatchEvent(new Event('resize'));
  }
  ngOnDestroy(): void {
    if (this.worksheets.length === 0) {
      this.excelTable?.destroy();
    }

    if ($(this.vsSheet.nativeElement)) {
      $(this.vsSheet.nativeElement).remove();
    }
    $('.input-clear-icon').off('click');
    $('.jexcel_search').off('keyup');
  }
  setReadOnlyPseudoCode() {
    this.readOnly = this.columns[11]?.readOnly ?? false;
  }

  closeDialog() {
    this.displayModal = false;
  }
  addRow() {
    const jexcelInstance = this.vsSheet.nativeElement.jexcel;
    const element = $('.jexcel_tab_link.selected');
    if (element && element[0]) {
      jexcelInstance[element.attr('data-spreadsheet') ?? 0].insertRow(10);
    } else {
      this.excelTable.insertRow(10);
    }
    const totalRows = jexcelInstance.options.data.length;
    const pageSize = 100;
    const lastPage = Math.ceil(totalRows / pageSize);

    jexcelInstance.loadPage(lastPage);
    setTimeout(() => {
      const table = jexcelInstance.content;
      table.scrollTop = table.scrollHeight;
    }, 0);
  }

  /**
   * [jQuery version]
   * Merging rows of nested headers.
   * @param {string} selector: The selector of the Jspreadsheet element.
   * @param {integer} colIndex: The index of the target column.
   *        If not specified or not integer, the row number column (left-most column) will be merged.
   */
  mergeHeaderRows_jQuery(selector, colIndex) {
    const $thead = $(selector).find('thead');

    if (Number.isInteger(colIndex)) {
      let $topTd;
      let $secondTd;
      /*if(colIndex===0){
        $topTd = $thead.find(`td`)[0];
        $secondTd = $thead.find(`td[class="jexcel_selectall"]`);
        $topTd.attr({
          'rowspan': 2,
          'class': 'jexcel_selectall'
        });
        //$secondTd.remove();
        return;
      }
      else */ if (colIndex === 1) {
        $topTd = $thead.find(`td[data-column="0,1"]`);
        $secondTd = $thead.find(`td[data-x="${colIndex}"]`);
      } else {
        $topTd = $thead.find(`td[data-column="${colIndex}"]`);
        $secondTd = $thead.find(`td[data-x="${colIndex}"]`);
      }

      $topTd
        .attr({
          'data-x': colIndex,
          rowspan: 2,
          title: $secondTd.text()
        })
        .html($secondTd.text());
      $secondTd.remove();
    } else {
      $thead.find('tr:first-child td:first-child').attr('rowspan', 2).addClass('jexcel_selectall');
      $thead.find('tr:nth-child(2) td.jexcel_selectall').remove();
    }
  }

  onLoad(instance) {
    if (this.initialLoad) {
      if (this.mergeHeader && this.mergeHeader.length > 0) {
        this.mergeHeader.forEach((e) => {
          this.mergeHeaderRows_jQuery('#spreadsheet', e);
        });
      }
      this.initialLoad = false;
    } else if (this.vsSheet.nativeElement) {
      this.vsSheet.nativeElement.getElementsByClassName('jexcel_content')[0].scrollTop = this.scrollPosition;
    }
    this.addAllowedRolesTooltips();
  }
}


